import { useMemo } from 'react';
import AdminNav from '@/components/admin/AdminNav';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { allRoutesComplete, getTranslatedUrl } from '@/lib/route-mapping';
import { getComprehensiveSEOData } from '@/lib/comprehensive-seo-data';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, Copy } from 'lucide-react';

const siteUrl = 'https://danielalbertlaw.com';

type RouteMap = Record<string, { en: string; es: string; vi: string }>

const URLIndex = () => {
  const rows = useMemo(() => {
    const entries = Object.entries(allRoutesComplete as RouteMap);
    return entries.map(([key, value]) => ({
      key,
      en: `${siteUrl}${value.en}`,
      es: `${siteUrl}${value.es}`,
      vi: `${siteUrl}${value.vi}`,
    }));
  }, []);

  const copyAll = () => {
    const lines: string[] = [];
    rows.forEach(r => {
      lines.push(r.en, r.es, r.vi);
    });
    navigator.clipboard.writeText(lines.join('\n'));
  };

  const exportCSV = () => {
    const csvEscape = (value: string) => {
      const v = value.replace(/\r?\n|\r/g, ' ').replace(/"/g, '""');
      return `"${v}"`;
    };

    const header = [
      'route_key',
      'language',
      'url',
      'title',
      'description',
      'h1',
      'canonical',
      'alternate_en',
      'alternate_es',
      'alternate_vi'
    ];

    const csvRows: string[] = [header.join(',')];

    // Generate a row for each language for each route
    rows.forEach(row => {
      const languages: Array<'en' | 'es' | 'vi'> = ['en', 'es', 'vi'];
      
      languages.forEach(lang => {
        const routeKey = row.key as keyof typeof allRoutesComplete;
        const routeData = allRoutesComplete[routeKey];
        const path = routeData[lang] as string;
        const seoData = getComprehensiveSEOData(path, lang);
        
        const langUrl = lang === 'en' ? row.en : lang === 'es' ? row.es : row.vi;
        
        csvRows.push([
          row.key,
          lang,
          langUrl,
          seoData.title,
          seoData.description,
          seoData.h1,
          langUrl, // canonical is the current URL
          row.en,  // alternate_en
          row.es,  // alternate_es
          row.vi   // alternate_vi
        ].map(csvEscape).join(','));
      });
    });

    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `seo-complete-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <SEO title="Índice de URLs (Admin)" description="Listado completo de URLs por idioma con exportación CSV" noindex />
      <div className="min-h-screen flex flex-col bg-background">
        <AdminNav />
        <main className="flex-1 py-8">
          <div className="container mx-auto px-4">
            <Card>
              <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>Índice de URLs</CardTitle>
                  <CardDescription>Generado desde el mapeo real de rutas. Incluye EN/ES/VI con dominio completo.</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={copyAll} variant="secondary">
                    <Copy className="h-4 w-4 mr-2" /> Copiar todas
                  </Button>
                  <Button onClick={exportCSV}>
                    <Download className="h-4 w-4 mr-2" /> Exportar CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="w-full overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Route Key</TableHead>
                        <TableHead>URL (EN)</TableHead>
                        <TableHead>URL (ES)</TableHead>
                        <TableHead>URL (VI)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.key}>
                          <TableCell className="font-mono text-xs">{r.key}</TableCell>
                          <TableCell className="text-xs">
                            <a href={r.en} target="_blank" rel="noopener noreferrer" className="underline">
                              {r.en}
                            </a>
                          </TableCell>
                          <TableCell className="text-xs">
                            <a href={r.es} target="_blank" rel="noopener noreferrer" className="underline">
                              {r.es}
                            </a>
                          </TableCell>
                          <TableCell className="text-xs">
                            <a href={r.vi} target="_blank" rel="noopener noreferrer" className="underline">
                              {r.vi}
                            </a>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default URLIndex;
